import React, { useContext, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import {formatNumber} from "@jutro/components";
import { IntlContext } from '@jutro/locale';
import CoverageTermsIterableComponent from '../../CoverageTermsIterableComponent/CoverageTermsIterableComponent';
import metadata from './HomeCoverageTable.metadata.json5';
import messages from './HomeCoverageTable.messages';
import styles from './HomeCoverageTable.module.scss';
import AdditionalInterestComponent from "../../AdditionalInterestComponent/AdditionalInterestComponent";

const getCell = (items, index, property) => {
    return !_.isObject(items[property.id]) ? items[property.id] : null;
};

const getLimitDeductible = (items, property) => {
    let amount = "-";
    items.terms.forEach(function(term) {
        if (term.name.toUpperCase() === property.toUpperCase()) {
            amount = term.amount;
        }
    });
    return amount;
};


const getDiscount = (items, index, property) => {
    return items['category'];
};
function HomeCoverageTable(props) {
    const { value: hoCoverages, isMarineCov } = props;
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);

    const trimCharsAnsSpecialChars = (userText) => {
        const regExpr = /[^0-9 ]/g;
        return userText.replace(regExpr, '');
    }

    const getCellCoverageDeductible = (rowData) => {
        if (_.isEmpty(rowData.deductible)) {
            return (
                <span>
                    {getLimitDeductible(rowData, 'Deductible')}
                </span>
            );
        }
        else {
            const formattedDeductible = formatNumber(intl, { format: 'decimal', showFractions: true}, trimCharsAnsSpecialChars(rowData.deductible));
            return (
                <span>
                    {formattedDeductible}
                </span>
            );
        }
    };


    const getCellCoverageLimit = (rowData) => {
        if (_.isEmpty(rowData.limit)) {
            if (rowData.terms.length === 0) {
                return (
                    <span>
                        {translator(messages.included)}
                    </span>
                );
            }
            return (
                <span>
                    {getLimitDeductible(rowData, 'Limit')}
                </span>
            );
        }
        let formattedLimit;
        if (rowData.limit.match(/\d{1,}/g) === null) {
            formattedLimit = rowData.limit;
        } else {
            formattedLimit = formatNumber(intl, { format: 'decimal', showFractions: true}, trimCharsAnsSpecialChars(rowData.limit));
        }
        return (
            <span>
                {formattedLimit}
            </span>
        );
    };


    const getApplied = (rowData) => {
        return rowData['discountApplied']
    }

   const generateScheduledItemsCoverageOverrides = useCallback(() => {
       const overrides = isMarineCov ? [] : hoCoverages.scheduledItems.map((coverage, cindex) => {
            return {
                [`scheduledItemsCoverageTerm${cindex}`]: {
                    label: coverage.description,
                    value: coverage.value
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [hoCoverages.scheduledItems]);

    const updateCoverages = () => {
        let combinedCoverages = [];
        combinedCoverages.concat(hoCoverages.sectionICoverables, hoCoverages.sectionIICoverables);
        // console.log(combinedCoverages);
        return combinedCoverages;
    };

    const filterEndorsements = (coverages) => {
        return _.filter(coverages, (cov) => cov.categoryCodeIdentifier !== 'HO_Endorsement_EY');
    }

    const overrides = {
        '@field': {
            readOnly: true
        },
        dwellingCoverageTable: {
            data: isMarineCov ? hoCoverages : filterEndorsements(hoCoverages.coverages)
        },
        dwellingEndorsementTable: {
            data: hoCoverages.endorsements,
            visible: !_.isEmpty(hoCoverages.endorsements)
        },
        additionalInterestTable: {
            data: hoCoverages.addlInterests,
            visible: !_.isEmpty(hoCoverages.addlInterests)
        },
        dwellingDiscountTable: {
            data: hoCoverages.discounts,
            visible: !_.isEmpty(hoCoverages.discounts)
        },
        sectionICoverables: {
            visible: !_.isEmpty(hoCoverages.sectionICoverages)
        },
        sectionIICoverables: {
            visible: !_.isEmpty(hoCoverages.sectionIICoverages)
        },
        EndorsementCoverages: {
            visible: !_.isEmpty(hoCoverages.endorsements)
        },
        additionalLiabilityCoverables: {
            visible: !_.isEmpty(hoCoverages.additionalLiabilityCoverages)
        },
        additionalPropertyCoverables: {
            visible: !_.isEmpty(hoCoverages.additionalPropertyCoverages)
        },
        scheduledItemsCoverables: {
            visible: !_.isEmpty(hoCoverages.scheduledItems)
        },
        sectionICoverableTitle: {
            value: !_.isEmpty(hoCoverages.sectionICoverages)
                ? hoCoverages.sectionICoverages[0].coverageCategory
                : undefined
        },
        sectionIICoverableTitle: {
            value: !_.isEmpty(hoCoverages.sectionIICoverages)
                ? hoCoverages.sectionIICoverages[0].coverageCategory
                : undefined
        },
        EndorsementCoverableTitle: {
             value: !_.isEmpty(hoCoverages.endorsements)
                            ? hoCoverages.endorsements[0].coverageCategory
                            : undefined
        },
        additionalLiabilityCoverableTitle: {
            value: !_.isEmpty(hoCoverages.additionalLiabilityCoverages)
                ? hoCoverages.additionalLiabilityCoverages[0].coverageCategory
                : undefined
        },
        additionalPropertyCoverableTitle: {
            value: !_.isEmpty(hoCoverages.additionalPropertyCoverages)
                ? hoCoverages.additionalPropertyCoverages[0].coverageCategory
                : undefined
        },
        scheduledItemsCoverableTitle: {
            value: !_.isEmpty(hoCoverages.scheduledItems)
                ? translator(messages.scheduledItems)
                : undefined
        },
        ...generateScheduledItemsCoverageOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            coveragetermsiterablecomponent: CoverageTermsIterableComponent,
            additionalinterestcomponent: AdditionalInterestComponent
        },
        resolveCallbackMap: {
            getCell,
            getLimitDeductible,
            getDiscount,
            getCellCoverageLimit,
            getCellCoverageDeductible,
            getApplied
        },
        resolveClassNameMap: styles
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.componentContent, hoCoverages, id, path, overrides);
        },
        [hoCoverages, overrides]
    );

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={hoCoverages}
            overrideProps={overrides}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            resolveValue={readValue}
        />
    );
}

HomeCoverageTable.propTypes = {
    value: PropTypes.shape({
        scheduledItems: PropTypes.arrayOf(PropTypes.shape({})),
        sectionICoverages: PropTypes.arrayOf(PropTypes.shape({})),
        sectionIICoverages: PropTypes.arrayOf(PropTypes.shape({})),
        additionalLiabilityCoverages: PropTypes.arrayOf(PropTypes.shape({})),
        additionalPropertyCoverages: PropTypes.arrayOf(PropTypes.shape({}))
    }).isRequired
};

export default HomeCoverageTable;
